import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    FaBalanceScale, 
    FaUserPlus, 
    FaChartLine, 
    FaShieldAlt, 
    FaCalculator, 
    FaCheckCircle, 
    FaQuoteLeft, 
    FaUsers, 
    FaArrowRight,
    FaRegLightbulb
} from 'react-icons/fa';
import './hrService.css';
import defaultHrImg from '../../Assets/service/hrconsulting.jpg';

const HrServiceDetails = ({ img }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const imageToUse = img || defaultHrImg;

    const pillars = [
        {
            id: 1,
            title: "Compliance & Operations",
            icon: <FaBalanceScale />,
            items: [
                {
                    title: "Payroll Management",
                    desc: "Seamless, accurate, and timely compensation cycles."
                },
                {
                    title: "Labour Compliance",
                    desc: "Navigating Provincial and Federal legal requirements."
                },
                {
                    title: "Policy Formation",
                    desc: "Custom employee handbooks and SOPs."
                }
            ]
        },
        {
            id: 2,
            title: "Growth & Talent",
            icon: <FaUserPlus />,
            items: [
                {
                    title: "Recruitment Services",
                    desc: "Finding the right fit, not just the right resume."
                },
                {
                    title: "Talent Management",
                    desc: "Developing your high-potential leaders."
                },
                {
                    title: "Onboarding",
                    desc: "Setting new hires up for day-one success."
                }
            ]
        },
        {
            id: 3,
            title: "Culture & Strategy",
            icon: <FaChartLine />,
            items: [
                {
                    title: "Organizational Development",
                    desc: "Scaling your structure for the future."
                },
                {
                    title: "Engagement Surveys",
                    desc: "Measuring what matters to your team."
                },
                {
                    title: "Performance Systems",
                    desc: "Moving beyond the \"annual review.\""
                }
            ]
        }
    ];

    const whyHr = [
        {
            id: 1,
            title: "Financial Accuracy",
            icon: <FaCalculator />,
            desc: "We ensure your payroll and benefits align perfectly with your tax strategy."
        },
        {
            id: 2,
            title: "Risk Mitigation",
            icon: <FaShieldAlt />,
            desc: "We protect you from costly provincial labor disputes and compliance penalties."
        },
        {
            id: 3,
            title: "Data-Driven Decisions",
            icon: <FaRegLightbulb />,
            desc: "We use engagement surveys and OD initiatives to turn \"gut feelings\" into actionable growth strategies."
        }
    ];

    return (
        <div className="hr-container">
            {/* ── Intro Header & Media ── */}
            <section className="hr-hero-intro">
                <div className="hr-media-box">
                    <img src={imageToUse} alt="Strategic HR Solutions" className="hr-media-img" />
                    <div className="hr-stamp-badge">
                        <div className="hr-stamp-icon">
                            <FaUsers />
                        </div>
                        <div className="hr-stamp-text">
                            <strong>HR Solutions Lead</strong>
                            <span>People &amp; Profits Integration</span>
                        </div>
                    </div>
                </div>

                <div className="hr-intro-content">
                    <span className="hr-pill-tag">Strategic Human Resources Consulting</span>
                    <h1 className="hr-main-title">People &amp; Profits: Meet Our HR Solutions Lead</h1>
                    <h2 className="hr-subtitle">Bridging the Gap Between Your Bottom Line and Your Best Assets</h2>
                    <p className="hr-lead-para">
                        While numbers tell the story of where your business has been, your people determine where it’s going. We are thrilled to expand our suite of services beyond traditional CPA offerings to include Strategic Human Resources Consulting. Whether you are a startup hiring your first employee or an established firm navigating complex provincial regulations, we provide the infrastructure to keep your team compliant, engaged, and performing at their peak.
                    </p>
                </div>
            </section>

            {/* ── Our HR Service Pillars ── */}
            <section className="hr-pillars-section">
                <div className="hr-section-header">
                    <span className="hr-pill-tag">Core Service Offerings</span>
                    <h2>Our HR Service Pillars</h2>
                    <p>Tailored human resource solutions built around compliance, growth, and organizational strategy.</p>
                </div>

                <div className="hr-pillars-grid">
                    {pillars.map((pillar) => (
                        <div key={pillar.id} className="hr-pillar-card">
                            <div className="hr-pillar-card-top">
                                <div className="hr-pillar-icon-wrap">
                                    {pillar.icon}
                                </div>
                                <h3 className="hr-pillar-title">{pillar.title}</h3>
                            </div>
                            <ul className="hr-pillar-items">
                                {pillar.items.map((item, idx) => (
                                    <li key={idx} className="hr-pillar-item">
                                        <FaCheckCircle className="hr-check-bullet" />
                                        <div className="hr-item-content">
                                            <strong>{item.title}</strong>
                                            <span>{item.desc}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Why HR in a CPA Firm? ── */}
            <section className="hr-why-cpa-section">
                <div className="hr-section-header">
                    <span className="hr-pill-tag" style={{ color: '#60a5fa', borderColor: 'rgba(96, 165, 250, 0.3)', background: 'rgba(96, 165, 250, 0.1)' }}>The CPA Advantage</span>
                    <h2>Why HR in a CPA Firm?</h2>
                    <p>
                        Most business challenges are human challenges in disguise. By integrating HR with our financial services, we offer a 360-degree view of your business health:
                    </p>
                </div>

                <div className="hr-cpa-grid">
                    {whyHr.map((item) => (
                        <div key={item.id} className="hr-cpa-card">
                            <div className="hr-cpa-badge">
                                {item.icon}
                            </div>
                            <h3>{item.title}</h3>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Quote Section ── */}
            <section className="hr-quote-section">
                <div className="hr-quote-box">
                    <FaQuoteLeft className="hr-quote-icon" />
                    <p className="hr-quote-text">
                        "Efficiency is doing things right; effectiveness is doing the right things. Our goal is to ensure your HR policies aren't just a safety net, but a competitive advantage."
                    </p>
                    <span className="hr-quote-author">— HR Solutions Lead, Oriental Business Solution</span>
                </div>
            </section>

            {/* ── CTA Banner ── */}
            <section className="hr-cta-banner">
                <div className="hr-cta-inner">
                    <h3>Ready to Elevate Your Workplace Strategy?</h3>
                    <p>Connect with our HR Solutions Lead today to integrate your people strategy with financial performance.</p>
                    <Link to="/contact" className="hr-cta-btn">
                        Get In Touch <FaArrowRight />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default HrServiceDetails;
